import { useMemo } from "react";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs } from "@/core/utils/date";
import { formatDate } from "@/core/utils/date";
import { formatMoneyCents } from "@/core/utils/money";
import { formatPhoneNumber } from "@/core/utils/phone";
import { Payout } from "@/customer/customer";

interface PayoutInfoProps {
  label: string;
  value: string | number;
  className?: string;
}

const PayoutInfo = ({ label, value, className }: PayoutInfoProps) => (
  <Stack gap="sm" fullWidth className={className}>
    <Typography variant="subtitleW3" textColor="tertiary">
      {label}
    </Typography>
    <Typography variant="bodyW1">{value}</Typography>
  </Stack>
);

interface PayoutItemProps {
  payout: Payout;
}

const PayoutItem = ({ payout }: PayoutItemProps) => (
  <Stack direction="row" fullWidth>
    <PayoutInfo
      label="Referral"
      value={
        payout.referral?.potential_customer_name ||
        formatPhoneNumber(payout.referral?.potential_customer_phone_number) ||
        "-"
      }
      className="flex-1"
    />
    <PayoutInfo
      label="Date"
      value={formatDate(payout.created_at)}
      className="w-[108px]"
    />
    <PayoutInfo
      label="Amount"
      value={formatMoneyCents(payout.amount) || "-"}
      className="w-[81px]"
    />
  </Stack>
);

interface PayoutListProps {
  payouts: Payout[];
}

export const PayoutList = ({ payouts }: PayoutListProps) => {
  const sortedPayouts = useMemo(
    () =>
      [...payouts].sort((a, b) =>
        dayjs(b.created_at).diff(dayjs(a.created_at)),
      ),
    [payouts],
  );

  return (
    <Stack className="h-full px-6 pr-1" fullWidth>
      <ScrollArea className="min-h-0 w-full flex-1 pr-5">
        <Stack gap="sm" fullWidth className="pb-8 pt-4">
          {sortedPayouts.map((payout) => (
            <PayoutItem key={payout.id} payout={payout} />
          ))}
        </Stack>
      </ScrollArea>
    </Stack>
  );
};
